import Appsignal from '@appsignal/javascript';
import { plugin as networkPlugin } from '@appsignal/plugin-breadcrumbs-network';
import { plugin as pathPlugin } from '@appsignal/plugin-path-decorator';
import { plugin as windowPlugin } from '@appsignal/plugin-window-events';

import { Ownership } from 'lib/generated_constants/ownership';
import { ENV } from 'lib/react_on_rails/env';

import QueryString from '../query_string';

import { extractUriDetails } from './extract_uri_details';
import { ignoreErrors } from './ignore_errors';

export const DEFAULT_NAMESPACE = 'frontend';

export function initializeAppsignal({
  owner,
  env,
}: {
  owner?: Ownership;
  env?: typeof ENV;
}) {
  if (!env || !env.APPSIGNAL_ENABLED) return null;

  const appsignal = new Appsignal({
    key: env.APPSIGNAL_JS_API_KEY,
    namespace: DEFAULT_NAMESPACE,
    revision: env.APP_COMMIT ?? undefined,
    ignoreErrors: env.APPSIGNAL_JS_IGNORE_ENABLED ? ignoreErrors : [],
  });

  appsignal.addDecorator((span) => {
    const uriDetails = extractUriDetails(window.location.pathname);
    span.setAction(uriDetails.action);

    if (Object.keys(uriDetails.params).length) {
      span.setParams(uriDetails.params);
    }

    if (window.location.search) {
      span.setParams(QueryString.parse(window.location.search));
    }

    return span;
  });

  appsignal.addDecorator((span) => {
    const tags: {
      owner?: string;
      company?: string;
      organization?: string;
      user_id?: string;
    } = {};

    if (owner) {
      tags.owner = owner;
    }
    if (env.TELEMETRY_USER?.organizationId) {
      tags.company = tags.organization =
        env.TELEMETRY_USER.organizationId.toString();
    }
    if (env.TELEMETRY_USER?.id) {
      tags.user_id = env.TELEMETRY_USER.id.toString();
    }

    return span.setTags(tags);
  });

  return appsignal;
}

function initializeDefaultAppsignal() {
  if (!ENV) return;

  const appsignal = initializeAppsignal({
    env: ENV,
  });

  if (!appsignal) return;

  const plugins = [networkPlugin, pathPlugin];

  if (ENV.APPSIGNAL_JS_WINDOW_EVENTS) {
    plugins.push(windowPlugin);
  }

  // May want to consider `xhrEnabled: false` option for the networkPlugin
  // as this seems to mostly capture third party scripts vs. internal fetch
  plugins.forEach((plugin) => appsignal.use(plugin()));

  return appsignal;
}

// oxlint-disable-next-line import/no-default-export
export default initializeDefaultAppsignal();
